import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Award, BarChart3, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlayerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [player, setPlayer] = useState(null);
  const [talentMetrics, setTalentMetrics] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  useEffect(() => {
    fetchPlayerData();
  }, [id]);

  const fetchPlayerData = async () => {
    try {
      const response = await axios.get(`${API}/players/${id}`);
      setPlayer(response.data);
      fetchTalentAnalysis(id);
    } catch (error) {
      toast.error('Failed to fetch player data');
      console.error('Error:', error);
    }
  };

  const fetchTalentAnalysis = async (playerId) => {
    setLoadingAnalysis(true);
    try {
      const response = await axios.get(`${API}/players/${playerId}/talent-analysis`);
      setTalentMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch talent analysis:', error);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  if (!player) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ 
        background: theme === 'dark' 
          ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      {/* Header */}
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button 
            onClick={() => navigate('/')} 
            className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors mb-4`}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Player Info Card */}
          <Card className={`glass-card lg:col-span-1 ${theme === 'light' ? 'bg-white/95' : ''}`} data-testid="player-info-card">
            <div className="text-center space-y-6">
              <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-4xl font-bold">
                {player.name.split(' ').map(n => n[0]).join('')}
              </div>
              
              <div>
                <h1 className={`text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-testid="player-name">{player.name}</h1>
                <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} data-testid="player-role">{player.role}</p>
              </div>
              
              {player.talent_score && (
                <div className="p-6 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-600/20 border border-green-500/30">
                  <div className="text-5xl font-bold gradient-text mb-2" data-testid="talent-score">
                    {player.talent_score}
                  </div>
                  <div className="text-sm text-gray-400">Talent Score</div>
                  <Progress value={player.talent_score} className="mt-4" />
                </div>
              )}
              
              <div className="space-y-3 text-left">
                <div className="flex justify-between">
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Age</span>
                  <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-testid="player-age">{player.age}</span>
                </div>
                <div className="flex justify-between">
                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Country</span>
                  <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-testid="player-country">{player.country}</span>
                </div>
                {player.batting_style && (
                  <div className="flex justify-between">
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Batting</span>
                    <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-testid="player-batting">{player.batting_style}</span>
                  </div>
                )}
                {player.bowling_style && (
                  <div className="flex justify-between">
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Bowling</span>
                    <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-testid="player-bowling">{player.bowling_style}</span>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Stats and Analysis */}
          <div className="lg:col-span-2 space-y-6">
            {/* Bio */}
            {player.bio && (
              <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`} data-testid="player-bio">
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>About</h2>
                <p className={`leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{player.bio}</p>
              </Card>
            )}

            {/* Stats */}
            <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`} data-testid="player-stats">
              <div className="flex items-center space-x-2 mb-6">
                <BarChart3 className="w-6 h-6 text-green-400" />
                <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Career Statistics</h2>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {Object.entries(player.stats || {}).map(([key, value]) => (
                  <div key={key} className={`text-center p-4 rounded-lg ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                    <div className="text-3xl font-bold gradient-text mb-2" data-testid={`stat-${key}`}>{value}</div>
                    <div className={`text-sm capitalize ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{key.replace('_', ' ')}</div>
                  </div>
                ))}
              </div>
            </Card>

            {/* AI Talent Analysis */}
            <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`} data-testid="talent-analysis">
              <div className="flex items-center space-x-2 mb-6">
                <Sparkles className="w-6 h-6 text-yellow-400" />
                <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>AI Talent Metrics</h2>
              </div>
              
              {loadingAnalysis ? (
                <div className="flex items-center justify-center py-8">
                  <div className="spinner"></div>
                </div>
              ) : talentMetrics ? (
                <div className="space-y-6">
                  {/* Metrics Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 text-center">
                      <div className="text-3xl font-bold text-blue-400 mb-1" data-testid="metric-consistency">
                        {talentMetrics.metrics.consistency}
                      </div>
                      <div className="text-xs text-gray-400">Consistency</div>
                      <Progress value={talentMetrics.metrics.consistency} className="mt-2 h-1" />
                    </div>
                    
                    <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 text-center">
                      <div className="text-3xl font-bold text-green-400 mb-1" data-testid="metric-impact">
                        {talentMetrics.metrics.impact}
                      </div>
                      <div className="text-xs text-gray-400">Impact</div>
                      <Progress value={talentMetrics.metrics.impact} className="mt-2 h-1" />
                    </div>
                    
                    <div className="p-4 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 text-center">
                      <div className="text-3xl font-bold text-orange-400 mb-1" data-testid="metric-form">
                        {talentMetrics.metrics.form}
                      </div>
                      <div className="text-xs text-gray-400">Current Form</div>
                      <Progress value={talentMetrics.metrics.form} className="mt-2 h-1" />
                    </div>
                    
                    <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-center">
                      <div className="text-3xl font-bold text-purple-400 mb-1" data-testid="metric-potential">
                        {talentMetrics.metrics.potential}
                      </div>
                      <div className="text-xs text-gray-400">Potential</div>
                      <Progress value={talentMetrics.metrics.potential} className="mt-2 h-1" />
                    </div>
                  </div>
                  
                  {/* AI Highlights */}
                  <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                    <h3 className={`font-bold mb-3 flex items-center ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>
                      <Award className="w-5 h-5 mr-2" />
                      Key Strengths
                    </h3>
                    <div className={`leading-relaxed whitespace-pre-line ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`} data-testid="analysis-highlights">
                      {talentMetrics.highlights}
                    </div>
                  </div>
                </div>
              ) : (
                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Loading metrics...</p>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}